import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  MdWhatsapp, MdSend, MdHistory, MdSearch, MdSelectAll,
  MdCheckBox, MdCheckBoxOutlineBlank, MdPeople, MdDelete,
  MdCheckCircle, MdError, MdHourglassEmpty, MdExpandMore,
  MdExpandLess, MdClose, MdPersonAdd
} from 'react-icons/md';
import { clientesAPI, difusionAPI, plantillasAPI, leadsAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

/* ─── Layout ─── */
const Container = styled.div`width: 100%; max-width: 100%;`;
const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.xl}; flex-wrap: wrap; gap: ${p => p.theme.spacing.md};
`;
const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl}; color: ${p => p.theme.colors.textPrimary}; margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: #25D366; }
`;

/* ─── Tabs ─── */
const TabBar = styled.div`
  display: flex; gap: 2px; margin-bottom: ${p => p.theme.spacing.xl};
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 4px; box-shadow: ${p => p.theme.shadows.card}; width: fit-content;
`;
const Tab = styled.button`
  padding: 10px 20px; border: none; border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.active
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: ${p => p.active ? 700 : 400};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer;
  display: flex; align-items: center; gap: 6px; transition: all 0.15s;
  svg { font-size: 1.1rem; }
  &:hover { color: ${p => p.active ? 'white' : p.theme.colors.textPrimary}; }
`;

/* ─── Cards ─── */
const Grid2 = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: ${p => p.theme.spacing.xl};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;
const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl}; box-shadow: ${p => p.theme.shadows.card};
`;
const CardTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg}; color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0; font-weight: 700;
  padding-bottom: ${p => p.theme.spacing.md}; border-bottom: 1px solid ${p => p.theme.colors.border};
  display: flex; align-items: center; gap: 8px;
  svg { color: ${p => p.theme.colors.primary}; }
`;

/* ─── Tipo selector ─── */
const TipoBar = styled.div`
  display: flex; gap: 2px; margin-bottom: 12px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 3px; border: 1px solid ${p => p.theme.colors.border};
`;
const TipoBtn = styled.button`
  flex: 1; padding: 6px 10px; border: none;
  border-radius: calc(${p => p.theme.borderRadius.md} - 2px);
  background: ${p => p.active ? p.theme.colors.primary : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.textSecondary};
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: ${p => p.active ? 700 : 500};
  font-family: ${p => p.theme.fonts.primary}; cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  &:hover { color: ${p => p.active ? 'white' : p.theme.colors.textPrimary}; }
`;

/* ─── Clientes selector ─── */
const SearchRow = styled.div`
  display: flex; gap: 8px; margin-bottom: 12px; align-items: center;
`;
const SearchInput = styled.input`
  flex: 1; padding: 8px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const FilterSelect = styled.select`
  padding: 8px 10px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;
const SelectAllBtn = styled.button`
  padding: 8px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textSecondary}; font-size: ${p => p.theme.fontSizes.sm};
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center; gap: 4px;
  cursor: pointer; white-space: nowrap;
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;
const ContactList = styled.div`
  max-height: 340px; overflow-y: auto; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme.colors.border}; border-radius: 2px; }
`;
const ContactItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 14px;
  cursor: pointer; border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  background: ${p => p.selected ? p.theme.colors.primary + '08' : 'transparent'};
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.colors.background}; }
`;
const ContactCheck = styled.div`
  color: ${p => p.selected ? p.theme.colors.primary : p.theme.colors.textLight};
  font-size: 1.25rem; flex-shrink: 0;
  svg { display: block; }
`;
const ContactInfo = styled.div`flex: 1; min-width: 0;`;
const ContactNombre = styled.div`
  font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const ContactTel = styled.div`
  font-size: ${p => p.theme.fontSizes.xs}; color: ${p => p.theme.colors.textSecondary}; margin-top: 1px;
`;
const NoTelBadge = styled.span`
  font-size: 10px; padding: 1px 6px; border-radius: 99px; font-weight: 600;
  background: ${p => p.theme.colors.danger}15; color: ${p => p.theme.colors.danger};
`;
const TipoBadge = styled.span`
  font-size: 10px; padding: 2px 7px; border-radius: 99px; font-weight: 700; flex-shrink: 0;
  background: ${p => p.lead ? '#8B5CF620' : p.theme.colors.primary + '15'};
  color: ${p => p.lead ? '#8B5CF6' : p.theme.colors.primary};
`;
const SelectedCount = styled.div`
  margin-top: 8px; font-size: ${p => p.theme.fontSizes.xs}; color: ${p => p.theme.colors.textSecondary};
  display: flex; align-items: center; gap: 4px;
`;

/* ─── Compose ─── */
const FieldGroup = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: ${p => p.theme.spacing.lg};`;
const Label = styled.label`font-size: ${p => p.theme.fontSizes.sm}; font-weight: 600; color: ${p => p.theme.colors.textPrimary};`;
const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const PreviewBubble = styled.div`
  background: #dcf8c6; border-radius: 8px 8px 2px 8px; padding: 10px 14px;
  font-size: ${p => p.theme.fontSizes.sm}; color: #1a1a1a; white-space: pre-wrap;
  word-break: break-word; max-width: 320px; margin-left: auto;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
`;
const PreviewLabel = styled.div`
  font-size: 11px; color: ${p => p.theme.colors.textLight}; text-align: right; margin-bottom: 4px;
`;
const SendBtn = styled.button`
  width: 100%; padding: 14px;
  background: ${p => p.disabled
    ? p.theme.colors.border
    : 'linear-gradient(135deg, #25D366, #128C7E)'};
  color: white; border: none; border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.md}; font-weight: 700;
  font-family: ${p => p.theme.fonts.primary}; display: flex; align-items: center;
  justify-content: center; gap: 8px; cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s; margin-top: ${p => p.theme.spacing.lg};
  svg { font-size: 1.3rem; }
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 16px #25D36640; }
`;

/* ─── Resultado ─── */
const ResultCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl}; box-shadow: ${p => p.theme.shadows.card};
  margin-top: ${p => p.theme.spacing.xl};
`;
const ResultHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: ${p => p.theme.spacing.lg};
`;
const ResultStats = styled.div`display: flex; gap: ${p => p.theme.spacing.xl}; flex-wrap: wrap;`;
const StatBox = styled.div`text-align: center;`;
const StatNum = styled.div`font-size: ${p => p.theme.fontSizes.xxl}; font-weight: 800; color: ${p => p.color};`;
const StatLabel = styled.div`font-size: 11px; color: ${p => p.theme.colors.textSecondary}; font-weight: 600; text-transform: uppercase;`;
const DetalleList = styled.div`display: flex; flex-direction: column; gap: 6px; margin-top: ${p => p.theme.spacing.lg};`;
const DetalleItem = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 8px 12px;
  border-radius: ${p => p.theme.borderRadius.md}; background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
`;

/* ─── Historial ─── */
const HistorialList = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const HistorialCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight}; border-radius: ${p => p.theme.borderRadius.lg};
  box-shadow: ${p => p.theme.shadows.card}; overflow: hidden;
`;
const HistorialHeader = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 16px 20px;
  cursor: pointer; border-bottom: 1px solid ${p => p.open ? p.theme.colors.border : 'transparent'};
`;
const HistorialTitle = styled.div`
  flex: 1;
  h4 { font-size: ${p => p.theme.fontSizes.md}; font-weight: 700; color: ${p => p.theme.colors.textPrimary}; margin: 0 0 2px; }
  p { font-size: ${p => p.theme.fontSizes.xs}; color: ${p => p.theme.colors.textSecondary}; margin: 0; }
`;
const HistorialStats = styled.div`display: flex; gap: 12px; align-items: center;`;
const HBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 99px;
  background: ${p => p.bg}20; color: ${p => p.bg};
`;
const HistorialBody = styled.div`
  padding: ${p => p.open ? '16px 20px' : '0 20px'};
  max-height: ${p => p.open ? '400px' : '0'};
  overflow: hidden; transition: all 0.2s;
`;
const DestinatarioRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 6px 0;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  font-size: ${p => p.theme.fontSizes.sm};
  &:last-child { border-bottom: none; }
`;
const EmptyState = styled.div`
  text-align: center; padding: ${p => p.theme.spacing.xl} 0;
  color: ${p => p.theme.colors.textLight}; font-size: ${p => p.theme.fontSizes.sm};
  svg { font-size: 2.5rem; display: block; margin: 0 auto 12px; opacity: 0.4; }
`;

const TplSelect = styled.select`
  width: 100%; padding: 10px 12px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  margin-bottom: 12px;
`;
const VarSection = styled.div`margin-top: 12px;`;
const VarLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.xs}; font-weight: 700; color: ${p => p.theme.colors.textSecondary};
  text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 8px;
`;
const VarRow = styled.div`
  display: flex; align-items: center; gap: 10px; margin-bottom: 8px;
`;
const VarName = styled.div`
  font-family: monospace; font-size: 13px; font-weight: 700; color: ${p => p.theme.colors.primary};
  min-width: 40px;
`;
const VarInput = styled.input`
  flex: 1; padding: 8px 10px; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background}; color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;
const VarSelect = styled.select`
  padding: 8px 10px; border: 1px solid ${p => p.theme.colors.primary}40;
  border-radius: ${p => p.theme.borderRadius.md}; font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.primary}08; color: ${p => p.theme.colors.primary};
  cursor: pointer;
  &:focus { outline: none; }
`;
const TplBodyPreview = styled.div`
  background: ${p => p.theme.colors.background}; border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md}; padding: 10px 14px;
  font-size: ${p => p.theme.fontSizes.sm}; white-space: pre-wrap; word-break: break-word;
  margin-bottom: 12px; color: ${p => p.theme.colors.textSecondary};
`;
const InfoNote = styled.div`
  font-size: 11px; color: ${p => p.theme.colors.textLight}; margin-top: 6px;
  display: flex; align-items: center; gap: 4px;
`;

const extractVars = text => {
  if (!text) return [];
  return [...new Set([...text.matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1])))].sort((a,b) => a-b);
};
const resolvePreview = (text, vars) => {
  if (!text) return '';
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => vars[Number(n) - 1] || `{{${n}}}`);
};

const fmtDate = d => d ? new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/* ═══════════════════════════════════════════════════════════════════════════ */
const Difusion = () => {
  const { toast, confirm } = useModal();

  const [activeTab, setActiveTab]   = useState('nueva');
  const [clientes, setClientes]     = useState([]);
  const [leads, setLeads]           = useState([]);
  const [tipoDestinatario, setTipoDestinatario] = useState('clientes');
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());
  const [selectedLeadIds, setSelectedLeadIds]     = useState(new Set());
  const [busqueda, setBusqueda]     = useState('');
  const [filtro, setFiltro]         = useState('todos');
  const [titulo, setTitulo]         = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [plantillaId, setPlantillaId] = useState('');
  const [variables, setVariables]   = useState([]);
  const [enviando, setEnviando]     = useState(false);
  const [resultado, setResultado]   = useState(null);

  const [historial, setHistorial] = useState([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({});

  useEffect(() => {
    clientesAPI.getAll()
      .then(r => setClientes((r.data.data || []).filter(c => c.estado === 'Activo' && !c.padre_cliente_id)))
      .catch(() => {});
    leadsAPI.getAll()
      .then(r => setLeads(r.data.data || []))
      .catch(() => {});
    plantillasAPI.getAll()
      .then(r => setPlantillas((r.data.data || []).filter(p => p.status?.toLowerCase() === 'approved')))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'historial') loadHistorial();
  }, [activeTab]); // eslint-disable-line

  const loadHistorial = async () => {
    setHistorialLoading(true);
    try { const r = await difusionAPI.getAll(); setHistorial(r.data.data || []); }
    catch {}
    finally { setHistorialLoading(false); }
  };

  // Lista unificada según tipo seleccionado
  const contactosBase =
    tipoDestinatario === 'clientes' ? clientes.map(c => ({ ...c, _tipo: 'cliente' })) :
    tipoDestinatario === 'leads'    ? leads.map(l => ({ ...l, _tipo: 'lead' })) :
    [...clientes.map(c => ({ ...c, _tipo: 'cliente' })), ...leads.map(l => ({ ...l, _tipo: 'lead' }))];

  const filtrados = contactosBase.filter(c => {
    const matchBusqueda = c.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
                          c.telefono?.includes(busqueda);
    const matchFiltro = filtro === 'todos' ? true
      : filtro === 'con_tel' ? !!c.telefono
      : filtro === 'sin_tel' ? !c.telefono
      : true;
    return matchBusqueda && matchFiltro;
  });

  const isSelected = c => c._tipo === 'cliente' ? selectedClientIds.has(c.id) : selectedLeadIds.has(c.id);

  const toggleItem = (c) => {
    if (!c.telefono) return;
    if (c._tipo === 'cliente') {
      setSelectedClientIds(prev => {
        const next = new Set(prev);
        next.has(c.id) ? next.delete(c.id) : next.add(c.id);
        return next;
      });
    } else {
      setSelectedLeadIds(prev => {
        const next = new Set(prev);
        next.has(c.id) ? next.delete(c.id) : next.add(c.id);
        return next;
      });
    }
  };

  const toggleAll = () => {
    const conTelClientes = filtrados.filter(c => c.telefono && c._tipo === 'cliente').map(c => c.id);
    const conTelLeads    = filtrados.filter(c => c.telefono && c._tipo === 'lead').map(c => c.id);
    const allSelected =
      conTelClientes.every(id => selectedClientIds.has(id)) &&
      conTelLeads.every(id => selectedLeadIds.has(id)) &&
      (conTelClientes.length + conTelLeads.length) > 0;

    if (allSelected) {
      setSelectedClientIds(prev => { const n = new Set(prev); conTelClientes.forEach(id => n.delete(id)); return n; });
      setSelectedLeadIds(prev => { const n = new Set(prev); conTelLeads.forEach(id => n.delete(id)); return n; });
    } else {
      setSelectedClientIds(prev => { const n = new Set(prev); conTelClientes.forEach(id => n.add(id)); return n; });
      setSelectedLeadIds(prev => { const n = new Set(prev); conTelLeads.forEach(id => n.add(id)); return n; });
    }
  };

  const totalSelected = selectedClientIds.size + selectedLeadIds.size;

  const handleEnviar = async () => {
    if (!titulo.trim())      { toast('El título es obligatorio', 'error'); return; }
    if (totalSelected === 0) { toast('Selecciona al menos un destinatario', 'error'); return; }
    if (!plantillaId)        { toast('Selecciona una plantilla', 'error'); return; }

    const ok = await confirm(
      `¿Enviar la plantilla "${plantillaSel?.name}" a ${totalSelected} destinatario${totalSelected !== 1 ? 's' : ''}?`,
      { title: 'Confirmar difusión', confirmText: 'Enviar' }
    );
    if (!ok) return;

    setEnviando(true);
    setResultado(null);
    const payload = {
      titulo,
      tipo: 'plantilla',
      plantilla_nombre: plantillaSel.name,
      plantilla_idioma: plantillaSel.language,
      variables,
      cliente_ids: [...selectedClientIds],
      lead_ids: [...selectedLeadIds],
    };
    try {
      const r = await difusionAPI.enviar(payload);
      setResultado(r.data);
      if (r.data.data?.enviados > 0) toast(r.data.message, 'success');
      else toast(r.data.message, 'error');
    } catch (err) { toast(err.message || 'Error al enviar', 'error'); }
    finally { setEnviando(false); }
  };

  const handleDeleteHistorial = async (id) => {
    const ok = await confirm('¿Eliminar este registro del historial?', { title: 'Eliminar', confirmText: 'Eliminar', danger: true });
    if (!ok) return;
    try {
      await difusionAPI.delete(id);
      setHistorial(prev => prev.filter(h => h.id !== id));
    } catch (err) { toast(err.message || 'Error', 'error'); }
  };

  const handleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (expandedData[id]) return;
    try {
      const r = await difusionAPI.getById(id);
      setExpandedData(prev => ({ ...prev, [id]: r.data.data }));
    } catch {}
  };

  const plantillaSel = plantillas.find(p => (p.id || p.name) === plantillaId);
  const plantillaBody = plantillaSel?.components?.find(c => c.type === 'BODY')?.text || '';
  const plantillaVarNums = extractVars(plantillaBody);
  const previewText = resolvePreview(plantillaBody, variables);

  const handlePlantillaChange = (val) => {
    setPlantillaId(val);
    const tpl = plantillas.find(p => (p.id || p.name) === val);
    const body = tpl?.components?.find(c => c.type === 'BODY')?.text || '';
    const nums = extractVars(body);
    setVariables(Array(nums.length).fill(''));
  };

  const setVariable = (idx, val) => setVariables(prev => { const n = [...prev]; n[idx] = val; return n; });

  const conTelFiltrados = filtrados.filter(c => c.telefono).length;
  const allConTelSelected =
    conTelFiltrados > 0 &&
    filtrados.filter(c => c.telefono && c._tipo === 'cliente').every(c => selectedClientIds.has(c.id)) &&
    filtrados.filter(c => c.telefono && c._tipo === 'lead').every(c => selectedLeadIds.has(c.id));

  // Labels para la etiqueta de conteo
  const labelTipo = tipoDestinatario === 'clientes' ? 'clientes'
    : tipoDestinatario === 'leads' ? 'leads'
    : 'contactos';

  const totalConTel =
    tipoDestinatario === 'clientes' ? clientes.filter(c => c.telefono).length :
    tipoDestinatario === 'leads'    ? leads.filter(l => l.telefono).length :
    clientes.filter(c => c.telefono).length + leads.filter(l => l.telefono).length;

  return (
    <Container>
      <PageHeader>
        <Title><MdWhatsapp /> Difusión WhatsApp</Title>
      </PageHeader>

      <TabBar>
        <Tab active={activeTab === 'nueva'} onClick={() => setActiveTab('nueva')}>
          <MdSend /> Nueva difusión
        </Tab>
        <Tab active={activeTab === 'historial'} onClick={() => setActiveTab('historial')}>
          <MdHistory /> Historial {historial.length > 0 && `(${historial.length})`}
        </Tab>
      </TabBar>

      {/* ════ NUEVA DIFUSIÓN ════ */}
      {activeTab === 'nueva' && (
        <>
          <Grid2>
            {/* ── Selector de destinatarios ── */}
            <Card>
              <CardTitle><MdPeople /> Destinatarios</CardTitle>

              {/* Tipo: Clientes / Leads / Todos */}
              <TipoBar>
                <TipoBtn active={tipoDestinatario === 'clientes'} onClick={() => setTipoDestinatario('clientes')}>
                  <MdPeople /> Clientes
                </TipoBtn>
                <TipoBtn active={tipoDestinatario === 'leads'} onClick={() => setTipoDestinatario('leads')}>
                  <MdPersonAdd /> Leads
                </TipoBtn>
                <TipoBtn active={tipoDestinatario === 'todos'} onClick={() => setTipoDestinatario('todos')}>
                  <MdSelectAll /> Todos
                </TipoBtn>
              </TipoBar>

              <SearchRow>
                <SearchInput
                  placeholder={`Buscar ${labelTipo} o teléfono...`}
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                />
                <FilterSelect value={filtro} onChange={e => setFiltro(e.target.value)}>
                  <option value="todos">Todos</option>
                  <option value="con_tel">Con teléfono</option>
                  <option value="sin_tel">Sin teléfono</option>
                </FilterSelect>
                <SelectAllBtn onClick={toggleAll} title="Seleccionar/deseleccionar todos con teléfono">
                  <MdSelectAll />
                  {allConTelSelected ? 'Quitar' : 'Todos'}
                </SelectAllBtn>
              </SearchRow>

              <ContactList>
                {filtrados.length === 0 && (
                  <EmptyState style={{ padding: '24px 0' }}>Sin {labelTipo}</EmptyState>
                )}
                {filtrados.map(c => {
                  const selected = isSelected(c);
                  const tieneTel = !!c.telefono;
                  const esLead = c._tipo === 'lead';
                  return (
                    <ContactItem
                      key={`${c._tipo}-${c.id}`}
                      selected={selected}
                      onClick={() => toggleItem(c)}
                      style={{ opacity: tieneTel ? 1 : 0.5, cursor: tieneTel ? 'pointer' : 'not-allowed' }}
                    >
                      <ContactCheck selected={selected}>
                        {selected ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
                      </ContactCheck>
                      <ContactInfo>
                        <ContactNombre>{c.nombre}</ContactNombre>
                        <ContactTel>
                          {c.telefono || <NoTelBadge>Sin teléfono</NoTelBadge>}
                        </ContactTel>
                      </ContactInfo>
                      {tipoDestinatario === 'todos' && (
                        <TipoBadge lead={esLead}>{esLead ? 'Lead' : 'Cliente'}</TipoBadge>
                      )}
                    </ContactItem>
                  );
                })}
              </ContactList>

              <SelectedCount>
                <MdCheckCircle style={{ color: totalSelected > 0 ? '#25D366' : '#ccc' }} />
                {totalSelected} de {totalConTel} {labelTipo} seleccionados
                {tipoDestinatario === 'todos' && selectedClientIds.size > 0 && selectedLeadIds.size > 0 && (
                  <span style={{ marginLeft: 6, opacity: 0.7 }}>
                    ({selectedClientIds.size} clientes, {selectedLeadIds.size} leads)
                  </span>
                )}
              </SelectedCount>
            </Card>

            {/* ── Composición ── */}
            <Card>
              <CardTitle><MdWhatsapp /> Componer mensaje</CardTitle>

              <FieldGroup>
                <Label>Título (interno, no se envía)</Label>
                <Input
                  placeholder="Ej: Promo junio, Recordatorio de pago..."
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                />
              </FieldGroup>

              <>
                <FieldGroup>
                  <Label>Plantilla</Label>
                  <TplSelect value={plantillaId} onChange={e => handlePlantillaChange(e.target.value)}>
                    <option value="">— Selecciona una plantilla —</option>
                    {plantillas.map(p => (
                      <option key={p.id || p.name} value={p.id || p.name}>
                        {p.name} ({p.language})
                      </option>
                    ))}
                  </TplSelect>
                  {plantillas.length === 0 && (
                    <InfoNote>No hay plantillas aprobadas. Crea una en la sección Plantillas.</InfoNote>
                  )}
                </FieldGroup>

                {plantillaSel && (
                  <>
                    <TplBodyPreview>{plantillaBody}</TplBodyPreview>

                    {plantillaVarNums.length > 0 && (
                      <VarSection>
                        <VarLabel>Variables</VarLabel>
                        {plantillaVarNums.map((n, i) => (
                          <VarRow key={n}>
                            <VarName>{`{{${n}}}`}</VarName>
                            <VarSelect
                              value={variables[i]?.startsWith('{{') ? variables[i] : ''}
                              onChange={e => { if (e.target.value) setVariable(i, e.target.value); }}
                            >
                              <option value="">Texto fijo →</option>
                              <option value="{{nombre_cliente}}">Nombre del contacto</option>
                              <option value="{{telefono}}">Teléfono</option>
                            </VarSelect>
                            <VarInput
                              placeholder={`Valor para {{${n}}}`}
                              value={variables[i]?.startsWith('{{') ? '' : (variables[i] || '')}
                              onChange={e => setVariable(i, e.target.value)}
                            />
                          </VarRow>
                        ))}
                      </VarSection>
                    )}

                    {previewText && (
                      <div style={{ marginTop: 12 }}>
                        <PreviewLabel>Vista previa</PreviewLabel>
                        <PreviewBubble>{previewText}</PreviewBubble>
                      </div>
                    )}
                  </>
                )}
              </>

              <SendBtn
                onClick={handleEnviar}
                disabled={enviando || totalSelected === 0 || !titulo.trim() || !plantillaId}
              >
                <MdWhatsapp />
                {enviando
                  ? `Enviando a ${totalSelected} destinatario${totalSelected !== 1 ? 's' : ''}...`
                  : `Enviar a ${totalSelected} destinatario${totalSelected !== 1 ? 's' : ''}`}
              </SendBtn>
            </Card>
          </Grid2>

          {/* ── Resultado ── */}
          {resultado && (
            <ResultCard>
              <ResultHeader>
                <CardTitle style={{ margin: 0, borderBottom: 'none', padding: 0 }}>
                  <MdSend /> Resultado del envío
                </CardTitle>
                <button onClick={() => setResultado(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 20 }}>
                  <MdClose />
                </button>
              </ResultHeader>
              <ResultStats>
                <StatBox>
                  <StatNum color="#25D366">{resultado.data?.enviados || 0}</StatNum>
                  <StatLabel>Enviados</StatLabel>
                </StatBox>
                <StatBox>
                  <StatNum color="#FC8181">{resultado.data?.errores || 0}</StatNum>
                  <StatLabel>Errores</StatLabel>
                </StatBox>
              </ResultStats>
              <DetalleList>
                {(resultado.data?.detalles || []).map((d, i) => (
                  <DetalleItem key={i}>
                    {d.estado === 'enviado'
                      ? <MdCheckCircle style={{ color: '#25D366', fontSize: '1.2rem', flexShrink: 0 }} />
                      : <MdError style={{ color: '#FC8181', fontSize: '1.2rem', flexShrink: 0 }} />}
                    <span style={{ fontWeight: 600, flex: 1 }}>{d.nombre}</span>
                    {d.estado === 'error' && (
                      <span style={{ fontSize: 12, color: '#FC8181' }}>{d.mensaje}</span>
                    )}
                  </DetalleItem>
                ))}
              </DetalleList>
            </ResultCard>
          )}
        </>
      )}

      {/* ════ HISTORIAL ════ */}
      {activeTab === 'historial' && (
        <>
          {historialLoading && <EmptyState><MdHourglassEmpty />Cargando...</EmptyState>}
          {!historialLoading && historial.length === 0 && (
            <EmptyState><MdHistory />Sin difusiones enviadas aún.</EmptyState>
          )}
          <HistorialList>
            {historial.map(h => {
              const open = expandedId === h.id;
              const det = expandedData[h.id];
              return (
                <HistorialCard key={h.id}>
                  <HistorialHeader onClick={() => handleExpand(h.id)} open={open}>
                    <MdWhatsapp style={{ color: '#25D366', fontSize: '1.4rem', flexShrink: 0 }} />
                    <HistorialTitle>
                      <h4>{h.titulo}</h4>
                      <p>{fmtDate(h.created_at)} · {h.total_destinatarios} destinatario{h.total_destinatarios !== 1 ? 's' : ''}</p>
                    </HistorialTitle>
                    <HistorialStats>
                      {h.enviados > 0 && <HBadge bg="#25D366">{h.enviados} enviados</HBadge>}
                      {h.errores > 0 && <HBadge bg="#FC8181">{h.errores} errores</HBadge>}
                      {open ? <MdExpandLess style={{ color: '#aaa' }} /> : <MdExpandMore style={{ color: '#aaa' }} />}
                    </HistorialStats>
                    <button
                      onClick={e => { e.stopPropagation(); handleDeleteHistorial(h.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FC8181', fontSize: 18, padding: 4 }}>
                      <MdDelete />
                    </button>
                  </HistorialHeader>
                  <HistorialBody open={open}>
                    <div style={{ background: '#dcf8c620', border: '1px solid #dcf8c6', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1a1a1a', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {h.mensaje}
                    </div>
                    {det ? (
                      <div>
                        {det.destinatarios?.map(d => (
                          <DestinatarioRow key={d.id}>
                            {d.estado === 'enviado'
                              ? <MdCheckCircle style={{ color: '#25D366' }} />
                              : <MdError style={{ color: '#FC8181' }} />}
                            <span style={{ fontWeight: 600, flex: 1 }}>{d.nombre}</span>
                            <span style={{ fontSize: 11, color: '#aaa' }}>{d.telefono}</span>
                            {d.mensaje_error && <span style={{ fontSize: 11, color: '#FC8181', maxWidth: 200 }}>{d.mensaje_error}</span>}
                            {d.enviado_en && <span style={{ fontSize: 11, color: '#aaa' }}>{fmtDate(d.enviado_en)}</span>}
                          </DestinatarioRow>
                        ))}
                      </div>
                    ) : (
                      <EmptyState style={{ padding: '12px 0' }}>Cargando detalle...</EmptyState>
                    )}
                  </HistorialBody>
                </HistorialCard>
              );
            })}
          </HistorialList>
        </>
      )}
    </Container>
  );
};

export default Difusion;
